import { Button, Switch } from 'antd';
import SearchBox from 'components/molecules/SearchBox';
import { UserContext } from 'contexts/ConfigContext';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { queryClient } from 'services/reactQueryService';
import { getAllPlansVariablesType } from 'types/tradePromoOptimization';
import {
    compareUsers,
    generateErrorMsg,
    getConfig,
    sendNotification,
    transformFiltersV2,
    transformFiltersV3,
} from 'utils/helperMethods';
import { PlusSquareOutlined } from '@ant-design/icons';
import PlansIndexTableWrapper from 'components/template/TradePromoOptimization/PlansIndexTableWrapper';
import CreateEditPlanContainer from 'components/molecules/TradePromoOptimization/Modals/CreateEditPlanContainer';
import { ErrorMessages, tpoNotificationMessages, tpoReactQueryKeyConstants } from 'utils/Enums';
import { TPODataContext, initialPlanEditMetaData } from 'contexts/TPOContext';
import { tradePromoOptimizationUrls } from 'config/urls';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import NewGlobalFilter from 'components/molecules/common/NewGlobalFilter/NewGlobalFilter';
import { DimensionDropdownType } from 'types/pricingSimulator';
import _ from 'lodash';
import FilterRibbon from 'components/molecules/PricingSimulator/FilterRibbon/FilterRibbon';

const subHeader = '';

export type CreatePlanModalType = {
    open: boolean;
    isEdit: boolean;
    data: Record<string, any>;
};

const initialModalState = { open: false, data: {}, isEdit: false };

//TPO Plans index page
const Plans = () => {
    const { rbacConditions, rbacLoading, showSyncedPlans, setShowSyncedPlans, selectedPlanInfo, setPlanEditMetaData } =
        useContext(TPODataContext);

    const { appConfig } = useContext(UserContext);
    const tradePromoOptimization = getConfig('tradePromoOptimization', appConfig);
    const enableEditMode = tradePromoOptimization?.enableEditMode;
    const { planIndexFilterKeys, filterDimensionInfo } = tradePromoOptimization;
    const limit = tradePromoOptimization?.indexPageTableLimit;
    const [searchKey, setSearchKey] = useState<string>('');

    const [tableReactQueryVariables, setTableReactQueryVariables] = useState<getAllPlansVariablesType>({
        search_keyword: '',
        limit,
        offset: 0,
        sort_by: 'updated_at',
        sorting_order: 'desc',
        user_id: localStorage.getItem('email'),
        plan_type: null,
        show_synced_plans: showSyncedPlans,
        filters: {},
    });
    const [createEditPlanModal, setCreateEditPlanModal] = useState<CreatePlanModalType>({ ...initialModalState });
    const [selectedFilter, setSelectedFilter] = useState<any>(null);
    const [tempApplyFilterObject, setTempApplyFilterObject] = useState<any>({});
    // Use TPOContext filter state for persistence
    const {
        plansIndexAllCheckedAttribute,
        setPlansIndexAllCheckedAttribute,
        plansIndexAllSelectedValueForAllAttribute,
        setPlansIndexAllSelectedValueForAllAttribute,
        plansIndexApplyFilterObject,
        setPlansIndexApplyFilterObject,
        updatePlansFilterObjectWithToolsSharing,
    } = useContext(TPODataContext);

    const [availableFilters, setAvailableFilters] = useState<DimensionDropdownType[]>([]);

    //release plan
    const { mutate: releasePlanEditFn, loading: releasePlanEditLoading } = useCommonMutationHandler({
        url: `${tradePromoOptimizationUrls.getPlanEditMetaDataUrl}/${createEditPlanModal?.data?.plan_id}/edit/release`,
        method: 'post',
    });
    //acquire plan
    const { isPending: editPlanAcquireLoading, mutate: editPlanAcquireFn } = useMutation({
        mutationFn: async (inputData: any) => {
            //write onsuccess and onerror where mutate function is called
            try {
                const data = await axios({
                    method: 'post',
                    url: `${tradePromoOptimizationUrls.getPlanEditMetaDataUrl}/${inputData?.planId}/edit/acquire`,
                    data: { user_id: localStorage.getItem('email') },
                });
                return data;
            } catch (error) {
                throw error;
            }
        },
        onSuccess: (data) => {},
        onError: (error: any) => {},
    });
    //acquire plan mutation
    function acquireEditPlan(planId: number) {
        if (editPlanAcquireLoading) return;
        editPlanAcquireFn(
            { planId },
            {
                onSuccess: (data) => {
                    //if user is same, then it means that edit rights are acquired
                    if (compareUsers(data?.data?.edit_lock?.editing_user)) {
                        sendNotification('success', tpoNotificationMessages.PLAN_EDIT_ACQUIRE_SUCCESS);
                    } else {
                        sendNotification(
                            'error',
                            `User ${data?.data?.edit_lock?.editing_user} is currently editing this plan`,
                        );
                    }
                    setPlanEditMetaData({
                        editingUser: data?.data?.edit_lock?.editing_user,
                        sessionExpiresInSecs: data?.data?.edit_lock?.session_expires_in_sec,
                        startedAt: data?.data?.edit_lock?.started_at,
                        planId: data?.data?.plan_id,
                    });
                },
                onError: (error: any) => {
                    const errorMsg = generateErrorMsg(
                        String(error?.response?.data?.detail || ''),
                        ErrorMessages.GENERALIZED_ERROR_MSG,
                    );
                    //show error message
                    sendNotification('error', errorMsg);
                },
            },
        );
    }

    function releaseEditRights() {
        if (releasePlanEditLoading) return;
        const payload = {
            user_id: localStorage.getItem('email'),
        };
        releasePlanEditFn(payload, {
            onSuccess: (data) => {
                setPlanEditMetaData({
                    ...initialPlanEditMetaData,
                });
            },
            onError: (error: any) => {
                setPlanEditMetaData({
                    ...initialPlanEditMetaData,
                });
            },
        });
    }
    //called when clicked on cancel
    function resetModalStates() {
        if (createEditPlanModal?.isEdit && enableEditMode) releaseEditRights();
        setCreateEditPlanModal({ ...initialModalState, data: createEditPlanModal?.data });
    }

    //called when plan is created
    function onCreatePlanFinish(planId: number) {
        //acquire plan when create plan is done
        if (!createEditPlanModal?.isEdit) {
            acquireEditPlan(planId);
        }

        sendNotification(
            'success',
            createEditPlanModal?.isEdit
                ? tpoNotificationMessages.EDIT_PLAN_SUCCESS
                : tpoNotificationMessages.CREATE_PLAN_SUCCESS,
        );

        resetModalStates();
        queryClient.refetchQueries({
            exact: true,
            queryKey: [tpoReactQueryKeyConstants.GET_ALL_PLANS, tableReactQueryVariables],
        });
    }

    //called when clicked on search icon or enter
    const onSearch = (val: string) => {
        setTableReactQueryVariables({ ...tableReactQueryVariables, offset: 0, search_keyword: val });
    };

    const resetFilterObject = () => {
        const allSelectedObj: any = {};
        const selectAllObj: any = {};
        const newApplyFilterObj: any = {};
        planIndexFilterKeys.forEach((filter: any) => {
            allSelectedObj[filter.value] = new Set();
            newApplyFilterObj[filter.value] = {};
            selectAllObj[filter.value] = false;
        });
        setAvailableFilters(planIndexFilterKeys);

        // Reset Plans-specific states
        setPlansIndexAllCheckedAttribute(allSelectedObj);
        setPlansIndexApplyFilterObject(_.cloneDeep(newApplyFilterObj));
        setPlansIndexAllSelectedValueForAllAttribute(selectAllObj);

        // Immediately update table variables with empty filters
        const updatedFilters = transformFiltersV2(newApplyFilterObj, planIndexFilterKeys);
        setTableReactQueryVariables((prev) => ({
            ...prev,
            filters: updatedFilters,
            offset: 0,
        }));
    };

    // Initialize filters using Plans-specific TPOContext
    useEffect(() => {
        // Use Plans-specific filter management to avoid interference from other tabs
        updatePlansFilterObjectWithToolsSharing(planIndexFilterKeys);
        setAvailableFilters(planIndexFilterKeys);
    }, [planIndexFilterKeys]);

    const onFilterSaveClickHandler = (resultObj: any) => {
        setPlansIndexAllCheckedAttribute(resultObj.allCheckedAttribute);
        setPlansIndexAllSelectedValueForAllAttribute(resultObj.allSelectedValueForAllAttribute);
        setPlansIndexApplyFilterObject(resultObj.applyFilterObject);
    };

    // Update table variables when filters change
    useEffect(() => {
        const updatedFilters = transformFiltersV2(plansIndexApplyFilterObject, planIndexFilterKeys);
        setTableReactQueryVariables((prev) => ({ ...prev, filters: updatedFilters, offset: 0 }));
    }, [plansIndexApplyFilterObject]);

    useEffect(() => {
        setTableReactQueryVariables({
            ...tableReactQueryVariables,
            offset: 0,
            show_synced_plans: showSyncedPlans,
        });
    }, [showSyncedPlans]);

    const commonVariables = useMemo(() => {
        if (selectedFilter?.value) {
            const updatedFilters = transformFiltersV3(tempApplyFilterObject, planIndexFilterKeys, selectedFilter.value);

            return {
                attribute_type: selectedFilter.value,
                limit: 50,
                filters: updatedFilters,
                show_synced_plans: showSyncedPlans,
            };
        }
    }, [selectedFilter, showSyncedPlans]);

    return (
        <>
            <div className="flex absolute right-4 top-4 justify-between items-end">
                <div>
                    <h5 className={`font-xs m-0 font-normal text-fontGreyAlt `}>{subHeader} </h5>
                </div>

                <div className="flex items-center gap-2">
                    <SearchBox onSearch={onSearch} search={searchKey} setSearch={setSearchKey} />
                    <div className="flex items-center gap-2">
                        <p className="text-promoColour font-xxs">Operational Plans</p>
                        <Switch
                            onChange={(val) => {
                                setShowSyncedPlans(val);
                            }}
                            checked={showSyncedPlans}
                        />
                    </div>
                    <NewGlobalFilter
                        availableFilters={availableFilters}
                        allCheckedAttribute={plansIndexAllCheckedAttribute}
                        allSelectedValueForAllAttribute={plansIndexAllSelectedValueForAllAttribute}
                        applyFilterObject={plansIndexApplyFilterObject}
                        resetFilterObject={resetFilterObject}
                        onSubmitHandler={onFilterSaveClickHandler}
                        url={tradePromoOptimizationUrls.getPlanIndexFilterAttributes}
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                        tempApplyFilterObject={tempApplyFilterObject}
                        setTempApplyFilterObject={setTempApplyFilterObject}
                        apiVariables={commonVariables}
                        filterDimensionInfo={filterDimensionInfo}
                        showAttributePanelToLeft
                    />
                    <Button
                        disabled={!rbacConditions?.showCreatePlanButton || rbacLoading}
                        style={{ borderRadius: 4 }}
                        onClick={() => {
                            setCreateEditPlanModal({ open: true, data: {}, isEdit: false });
                        }}
                        type="primary"
                        icon={<PlusSquareOutlined />}
                    >
                        Create Plan
                    </Button>
                </div>
            </div>
            <div className="my-3">
                <FilterRibbon
                    allCheckedAttribute={plansIndexAllCheckedAttribute}
                    allSelectedValueForAllAttribute={plansIndexAllSelectedValueForAllAttribute}
                    applyFilterObject={plansIndexApplyFilterObject}
                    resetFilterObject={resetFilterObject}
                    onSubmitHandler={(obj: any) => {
                        setPlansIndexAllCheckedAttribute(obj.allCheckedAttribute);
                        setPlansIndexAllSelectedValueForAllAttribute(obj.allSelectedValueForAllAttribute);
                        setPlansIndexApplyFilterObject(obj.applyFilterObject);
                    }}
                />
            </div>
            <div className="my-2 border-b border-borderGreyAlt"></div>
            <PlansIndexTableWrapper
                setCreateEditPlanModal={setCreateEditPlanModal}
                setTableReactQueryVariables={setTableReactQueryVariables}
                tableReactQueryVariables={tableReactQueryVariables}
            />
            <CreateEditPlanContainer
                onSubmitFinish={onCreatePlanFinish}
                open={createEditPlanModal.open}
                isEdit={createEditPlanModal?.isEdit}
                data={createEditPlanModal?.data}
                onCancel={(e) => {
                    resetModalStates();
                }}
            />
        </>
    );
};

export default Plans;
